import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import socketService from '../services/socketService';
import { useAppStore } from '../store/appStore';
import { Notification } from '../types/notification.types';
import { ActivityLog } from '../types/activity.types';
import logger from '../utils/logger';

interface SocketContextValue {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  // (Bug_30) The legacy Login flow writes the authenticated user to
  // sessionStorage via the Context layer, NOT to the Zustand store, so
  // useAppStore(state => state.user) is null on every page load. That
  // meant SocketProvider never connected and every real-time push was
  // lost. Read from sessionStorage as the source of truth and fall back
  // to the Zustand store for forward-compat.
  const storeUser = useAppStore((state) => state.user);
  const sessionUserRaw = typeof window !== 'undefined' ? sessionStorage.getItem('user') : null;
  let user: any = storeUser;
  if (!user && sessionUserRaw) {
    try {
      user = JSON.parse(sessionUserRaw);
    } catch {
      user = null;
    }
  }
  const addNotification = useAppStore((state) => state.addNotification);
  const markNotificationAsRead = useAppStore((state) => state.markNotificationAsRead);
  const addActivity = useAppStore((state) => state.addActivity);
  const addToastNotification = useAppStore((state) => state.addToastNotification);

  /**
   * Handle new notification event from Socket.io
   */
  const handleNewNotification = useCallback(
    (notification: Notification) => {
      logger.debug('[Socket] New notification received:', notification);

      // Add notification to store
      addNotification(notification);

      // Show toast notification for high priority
      if (notification.priority === 'high' || notification.priority === 'urgent') {
        toast.info(notification.message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Also add to toast notifications
        addToastNotification({
          type: 'info',
          message: notification.message,
        });
      }
    },
    [addNotification, addToastNotification]
  );

  /**
   * Handle notification read event from Socket.io
   * (Sync across tabs)
   */
  const handleNotificationRead = useCallback(
    (data: { notificationId: string }) => {
      logger.debug('[Socket] Notification marked as read:', data.notificationId);
      markNotificationAsRead(data.notificationId);
    },
    [markNotificationAsRead]
  );

  /**
   * Handle new activity event from Socket.io
   */
  const handleNewActivity = useCallback(
    (activity: ActivityLog) => {
      logger.debug('[Socket] New activity received:', activity);
      addActivity(activity);
    },
    [addActivity]
  );

  /**
   * Connect to Socket.io server
   */
  const connect = useCallback(() => {
    if (!user) {
      console.warn('[Socket] Cannot connect without user session');
      return;
    }

    logger.debug('[Socket] Connecting...');
    socketService.connect();

    // (Bug_30) The backend listens for `join:organization` with the orgId
    // as the raw argument (server.js:58). Previously the FE emitted
    // `auth:join` with {organizationId}, so the FE never actually joined
    // the org room and every targeted io.to(orgId).emit broadcast was
    // silently dropped. Align both sides AND make sure the join fires
    // whether or not the connect event has already happened.
    const orgId =
      (user as any).organizationId ||
      (user as any).orgId ||
      (user as any).org_id;
    const joinOrg = () => {
      if (orgId) {
        logger.debug(`[Socket] join:organization ${orgId}`);
        socketService.emit('join:organization', orgId);
      }
    };

    // Setup event listeners
    socketService.on('connect', () => {
      logger.debug('[Socket] Connected');
      setIsConnected(true);
      joinOrg();
    });

    // If the socket finished connecting before this listener was attached,
    // join immediately so we don't sit out of the org room.
    if (socketService.isConnected()) {
      setIsConnected(true);
      joinOrg();
    }

    socketService.on('disconnect', () => {
      logger.debug('[Socket] Disconnected');
      setIsConnected(false);
    });

    socketService.on('notification:new', handleNewNotification);
    socketService.on('notification:read', handleNotificationRead);
    socketService.on('activity:new', handleNewActivity);
  }, [user, handleNewNotification, handleNotificationRead, handleNewActivity]);

  /**
   * Disconnect from Socket.io server
   */
  const disconnect = useCallback(() => {
    logger.debug('[Socket] Disconnecting...');
    socketService.disconnect();
    setIsConnected(false);
  }, []);

  /**
   * Auto-connect when user logs in
   */
  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);

  const value: SocketContextValue = {
    isConnected,
    connect,
    disconnect,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
